import React, { useState } from "react";
import Navbar from "../Navbar/index";
import Footer from "../Footer/index";
import {
  Main,
  ClientText,
  ClientNameInfo,
  LogoArea,
} from "../../pages/ClientDashboard/styled";
import arrowleft from "../../Assets/arrowleft.png";
import PartnerInfos from "../ParterInfos/index";
import CustoEconomia from "./CustoEconomia";
import ConsumeLine from "../ConsumeLine/index";
import MultiUnitSelector from "../MultiUnitSelector";
import ConsolidatedConsumeLine from "../ConsolidatedConsumeLine";
import { ViabilidadeAccordion } from "./ViabilidadeAccordion";
import { DemandaAccordion } from "./DemandaAccordion";
import { LastroWarning } from "./LastroWarning";
import { TaxHistorySection } from "./TaxHistorySection";

const DEMANDA_COMPANY_ID = "41c755d8-aa00-468c-a40a-35677d463d12";

interface PartnerInfosProps {
  nome: string;
  sigla: string;
  email?: string;
  cnpj: string;
  inicioLivre: string;
  logo: string;
  parterInfosTotal: any;
  canEdit?: boolean;
}

export interface DashboardPageLayoutProps {
  /** Título exibido no topo da página */
  title: string;
  /** Exibe o botão de voltar (usado na visão de parceiro/admin) */
  showBackButton?: boolean;
  /** Props repassadas para o componente PartnerInfos */
  partnerInfoProps: PartnerInfosProps;
  /** ID da empresa, usado no LastroWarning e na condição do DemandaAccordion */
  companyId: string;
  /** CNPJ da empresa, usado no LastroWarning */
  cnpj: string;
  /** Lista de pontos de medição */
  profiles: any[] | null;
  /** Dados da empresa, repassados para CustoEconomia */
  companyData: any;
  /**
   * Quando true e profiles existirem, exibe os gráficos de consumo e economia.
   * ClientDashboard: true quando profiles carregados.
   * CompanyDashboard: true apenas quando status === "ATIVO".
   */
  showCharts: boolean;
  /**
   * Quando true, exibe a mensagem de migração no lugar dos gráficos.
   * CompanyDashboard: true quando status !== "ATIVO".
   */
  showMigrationMessage?: boolean;
  /** Conteúdo extra renderizado ao final do container (ex: modais) */
  children?: React.ReactNode;
}

const DashboardPageLayout: React.FC<DashboardPageLayoutProps> = ({
  title,
  showBackButton = false,
  partnerInfoProps,
  companyId,
  cnpj,
  profiles,
  companyData,
  showCharts,
  showMigrationMessage = false,
  children,
}) => {
  const [viewMode, setViewMode] = useState<"single" | "multi">("single");
  const [selectedMultiUnitIds, setSelectedMultiUnitIds] = useState<string[]>(
    []
  );
  const canUseMultiUnits = (profiles?.length || 0) >= 2;

  const changeViewMode = (mode: "single" | "multi") => {
    setViewMode(mode);
    if (mode === "single") setSelectedMultiUnitIds([]);
  };

  const viewModeToggle = (
    <div
      className="dashboard-view-mode"
      role="group"
      aria-label="Modo de visualização"
    >
      <button
        type="button"
        className={`btn ${viewMode === "single" ? "btn-primary" : "btn-light"}`}
        onClick={() => changeViewMode("single")}
      >
        Por unidade
      </button>
      {canUseMultiUnits && (
        <button
          type="button"
          className={`btn ${
            viewMode === "multi" ? "btn-primary" : "btn-light"
          }`}
          onClick={() => changeViewMode("multi")}
        >
          Multiunidades
        </button>
      )}
    </div>
  );

  return (
    <Main>
      <Navbar changeColor="true" />
      <div
        className="container"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: showBackButton ? "20px" : undefined,
        }}
      >
        {showBackButton && (
          <LogoArea>
            <img
              className="back"
              src={arrowleft}
              alt="Voltar"
              onClick={() => window.history.back()}
            />
            <div className="back-area" onClick={() => window.history.back()}>
              <span>Voltar</span>
            </div>
          </LogoArea>
        )}

        <ClientText>{title}</ClientText>

        <ClientNameInfo>
          <PartnerInfos {...partnerInfoProps} />
        </ClientNameInfo>

        <LastroWarning companyId={companyId} cnpj={cnpj} />

        <hr />

        {companyId === DEMANDA_COMPANY_ID && <DemandaAccordion />}

        {showCharts && profiles && profiles.length > 0 ? (
          <>
            <CustoEconomia
              totalCativo={companyData?.sumCostCativo || 0}
              totalLivre={companyData?.sumCostLivre || 0}
              economy={companyData?.sumEconomy || 0}
              description="Custos e economia de todas as unidades"
            />
            <ViabilidadeAccordion profiles={profiles} />
            <TaxHistorySection
              measuringPointIds={profiles.map((profile) => profile.id)}
            />

            {viewMode === "single" ? (
              <ConsumeLine
                monthlyData={profiles}
                profiles={profiles}
                viewModeToggle={viewModeToggle}
              />
            ) : (
              <>
                <MultiUnitSelector
                  key={selectedMultiUnitIds.join(",") || "new"}
                  profiles={profiles}
                  initialSelectedIds={selectedMultiUnitIds}
                  onConfirm={setSelectedMultiUnitIds}
                  viewModeToggle={viewModeToggle}
                />
                {selectedMultiUnitIds.length >= 2 && (
                  <ConsolidatedConsumeLine selectedIds={selectedMultiUnitIds} />
                )}
              </>
            )}
          </>
        ) : (
          showMigrationMessage && (
            <div className="right-text-center">
              <h1>
                Histórico e gráficos serão disponibilizados após a migração ser
                concluída
              </h1>
            </div>
          )
        )}

        {profiles && profiles.length === 0 && (
          <TaxHistorySection measuringPointIds={[]} />
        )}

        {children}
      </div>
      <Footer />
    </Main>
  );
};

export default DashboardPageLayout;
